import { useContext, useCallback } from 'react';
import { ApartmentViewContext } from '../../../context/ApartmentViewContext';
import { getInteractionsConfig } from '../../../config';
import { arrangeModeName } from '../../../context/contextStrings';
import styles from '../../../../../styles/aprt.module.scss';

/**
 * Custom hook for handling compartment interactions (hover, click, styling)
 * Uses mode configuration to determine interaction behavior
 * @param {string} mode - Current view mode
 * @param {Array} arrangementVersions - Array of available versions per compartment (for arrange mode)
 * @returns {Object} - Interaction handlers and className getters
 */
export function useCompartmentInteractions(mode, arrangementVersions = []) {
	const interactionsConfig = getInteractionsConfig(mode);
	const {
		apartmentViewInteractions,
		setApartmentViewInteractions,
		userSelectedArrangements,
		setUrlComboParam,
	} = useContext(ApartmentViewContext);

	const {
		hoveredCompartmentInd,
		selectedCompartmentInd,
	} = apartmentViewInteractions;

	/**
	 * Get the appropriate className based on hover/selection state
	 */
	const getClassName = useCallback(
		(baseClass, selectedClass, highlightedClass, ind) => {
			if (hoveredCompartmentInd === ind) return highlightedClass;
			if (selectedCompartmentInd === ind) return selectedClass;
			return baseClass;
		},
		[hoveredCompartmentInd, selectedCompartmentInd]
	);

	const getListRowClassName = useCallback(
		(ind) => {
			return getClassName(
				styles.list__row_compartments,
				styles.list__row_selected,
				styles.list__row_highlight,
				ind
			);
		},
		[getClassName]
	);

	const getListDtClassName = useCallback(
		(ind) => {
			return getClassName(
				styles.compartmentsList__dt,
				styles.compartmentsList__dt_selected,
				styles.compartmentsList__dt_highlight,
				ind
			);
		},
		[getClassName]
	);

	const getListDdClassName = useCallback(
		(ind) => {
			return getClassName(
				styles.compartmentsList__dd,
				styles.compartmentsList__dd_selected,
				styles.compartmentsList__dd_highlight,
				ind
			);
		},
		[getClassName]
	);

	/**
	 * Handle hover state for compartments
	 * @param {number|null} ind - Compartment index (null to clear hover)
	 * @param {boolean} isOnPath - Whether hover is on the SVG path
	 */
	const handleRoomHover = useCallback(
		(ind, isOnPath = false) => {
			if (ind === null) {
				setApartmentViewInteractions(
					({ isCompartmentPathHovered, hoveredCompartmentInd, ...rest }) => ({
						isCompartmentPathHovered: false,
						hoveredCompartmentInd: null,
						...rest,
					})
				);
			} else {
				setApartmentViewInteractions(
					({ isCompartmentPathHovered, hoveredCompartmentInd, ...rest }) => ({
						isCompartmentPathHovered: isOnPath,
						hoveredCompartmentInd: ind,
						...rest,
					})
				);
			}
		},
		[setApartmentViewInteractions]
	);

	/**
	 * Handle click on compartment (default behavior - toggle selection)
	 * @param {number} ind - Compartment index
	 */
	const handleRoomClick = useCallback(
		(ind) => {
			if (selectedCompartmentInd === ind) {
				setApartmentViewInteractions(({ selectedCompartmentInd, ...rest }) => ({
					selectedCompartmentInd: null,
					...rest,
				}));
			} else {
				setApartmentViewInteractions(({ selectedCompartmentInd, ...rest }) => ({
					selectedCompartmentInd: ind,
					...rest,
				}));
			}
		},
		[selectedCompartmentInd, setApartmentViewInteractions]
	);

	/**
	 * Handle click in arrange mode - cycles through arrangement versions
	 * @param {number} ind - Compartment index (1-based)
	 */
	const handleRoomClickInArrangeMode = useCallback(
		(ind) => {
			const clickedCompartmentIndex = ind - 1;
			const availableNumberOfVersions = arrangementVersions[clickedCompartmentIndex];

			const updateSelectionArray = (prev) => {
				const newArr = [...prev];
				newArr[clickedCompartmentIndex] =
					(newArr[clickedCompartmentIndex] + 1) % availableNumberOfVersions;
				return newArr;
			};

			const newArrangementSelection = updateSelectionArray(userSelectedArrangements);

			// Encode combination to URL parameter
			const encodeCombination = (arrangementsArray, variantsPerElement) => {
				let encodedValue = 0;
				for (let i = 0; i < arrangementsArray.length; i++) {
					let product = 1;
					for (let j = i - 1; j >= 0; j--) {
						product *= variantsPerElement[j];
					}
					encodedValue += arrangementsArray[i] * product;
				}
				return encodedValue;
			};

			const encodedCombination = encodeCombination(
				newArrangementSelection,
				arrangementVersions
			);

			setUrlComboParam(encodedCombination);
		},
		[arrangementVersions, userSelectedArrangements, setUrlComboParam]
	);

	/**
	 * Get the appropriate click handler based on mode configuration
	 */
	const getClickHandler = useCallback(
		(ind) => {
			const { clickBehavior } = interactionsConfig;
			
			switch (clickBehavior) {
				case 'cycleVersion':
					return () => handleRoomClickInArrangeMode(ind);
				case 'openModal':
					// TODO: Implement modal opening logic
					return () => handleRoomClick(ind);
				case 'selectAndExpand':
					return () => handleRoomClick(ind);
				case 'selectOnly':
					return () => handleRoomClick(ind);
				case 'none':
					return () => {}; // Do nothing
				default:
					return () => handleRoomClick(ind);
			}
		},
		[interactionsConfig, handleRoomClick, handleRoomClickInArrangeMode]
	);

	/**
	 * Check if interactions are enabled for current mode
	 */
	const isInteractive = interactionsConfig.clickBehavior !== 'none';
	const isHoverEnabled = interactionsConfig.hoverBehavior !== 'none';

	return {
		handleRoomHover,
		handleRoomClick,
		handleRoomClickInArrangeMode,
		getClickHandler,
		getListRowClassName,
		getListDtClassName,
		getListDdClassName,
		hoveredCompartmentInd,
		selectedCompartmentInd,
		isInteractive,
		isHoverEnabled,
		interactionsConfig,
	};
}
